import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  FaArrowRight,
  FaCheckCircle,
  FaClock,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhone,
  FaShieldAlt,
  FaTools,
  FaCertificate,
  FaGraduationCap,
  FaClipboardCheck,
} from 'react-icons/fa';

import '../styles/company-contact.css';

import contactHeroImage from '../pics/pressurepumpsite.jpg';

const mapSrc =
  'https://www.google.com/maps?q=Lagos%2C%20Nigeria&z=12&output=embed';

const API_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:5000/api';


export default function ContactPage() {
  const [formMode, setFormMode] = useState('general');
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (sending) return;

    const formData = new FormData(e.target);

    const payload = {
      name: formData.get('name') || '',
      email: formData.get('email') || '',
      phone: formData.get('phone') || '',
      company: formData.get('company') || '',
      service: formData.get('service') || '',
      subject: formData.get('subject') || '',
      message: formData.get('message') || '',
      honeypot: formData.get('honeypot') || ''
    };

    setSending(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/contact`,
        payload,
        { timeout: 20000 }
      );

      if (response.data?.success) {
        setSubmitted(true);
      } else {
        setError(
          response.data?.message ||
          'We could not send your enquiry. Please try again.'
        );
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        'We could not send your enquiry. Please check your connection and try again.'
      );
    } finally {
      setSending(false);
    }
  };


  const resetForm = () => {
    setSubmitted(false);
    setError(null);
  };


  return (
    <div className="contact-page">
      <SEO
        title="Contact Us"
        description="Get in touch with Dofcan Oilserve Limited for crane operations, load testing, pressure testing, calibration and inspection services in the oil and gas industry."
        path="/contact"
      />

      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="contact-hero">

        <div className="contact-hero-pattern"></div>

        <div className="contact-container">

          {/* Breadcrumb */}

          <div className="contact-breadcrumb">

            <Link to="/">Dofcan Oilserve Limited</Link>

            <FaArrowRight />

            <strong>Contact</strong>

            <span className="contact-status">
              <span></span>
              Contact Desk Active
            </span>

          </div>


          {/* Hero Grid */}

          <div className="contact-hero-grid">

            <div className="contact-hero-content">

              <div className="contact-eyebrow">
                <span></span>
                Training • Inspection • Certification
              </div>

              <h1>
                Let&apos;s Start
                <br />
                The Conversation.
              </h1>

              <p>
                Contact Dofcan Oilserve Limited for training,
                inspection, certification, verification support,
                and project-related enquiries.
              </p>


              <div className="contact-hero-buttons">

                <button
                  type="button"
                  className="contact-btn-primary"
                  onClick={() =>
                    document
                      .getElementById('contact-form')
                      ?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                      })
                  }
                >
                  Send An Enquiry
                  <FaArrowRight />
                </button>


                <a
                  href="tel:+2348036726334"
                  className="contact-btn-secondary"
                >
                  Call Our Team
                  <FaPhone />
                </a>

              </div>

            </div>


            {/* Hero Image */}

            <div className="contact-hero-visual">

              <img
                src={contactHeroImage}
                alt="Dofcan Oilserve field operations"
              />

              <div className="contact-hero-overlay"></div>

              <div className="contact-hero-caption">

                <div className="contact-caption-icon">
                  <FaEnvelope />
                </div>

                <div>
                  <span>DOFCAN CONTACT DESK</span>

                  <strong>
                    Professional • Direct • Responsive
                  </strong>
                </div>

              </div>

            </div>

          </div>


          {/* =================================================
              HERO METRICS
          ================================================= */}

          <div className="contact-metrics">

            <div className="contact-metric">

              <div className="contact-metric-top">
                <span>Email</span>
                <FaEnvelope />
              </div>

              <strong>01</strong>

              <p>
                Direct email contact channel
              </p>

            </div>


            <div className="contact-metric">

              <div className="contact-metric-top">
                <span>Phone</span>
                <FaPhone />
              </div>

              <strong>01</strong>

              <p>
                Direct telephone contact
              </p>

            </div>


            <div className="contact-metric">

              <div className="contact-metric-top">
                <span>Location</span>
                <FaMapMarkerAlt />
              </div>

              <strong>LAGOS</strong>

              <p>
                Nigeria
              </p>

            </div>


            <div className="contact-metric">

              <div className="contact-metric-top">
                <span>Services</span>
                <FaTools />
              </div>

              <strong>03</strong>

              <p>
                Training • Inspection • Certification
              </p>

            </div>

          </div>


          {/* Directory */}

          <div className="contact-directory">

            <span>Contact Directory:</span>

            <button
              type="button"
              onClick={() =>
                document
                  .getElementById('contact-details')
                  ?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  })
              }
            >
              Contact Details
            </button>

            <button
              type="button"
              onClick={() =>
                document
                  .getElementById('contact-form')
                  ?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  })
              }
            >
              Send Enquiry
            </button>

            <button
              type="button"
              onClick={() =>
                document
                  .getElementById('contact-location')
                  ?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  })
              }
            >
              Find Us
            </button>

          </div>

        </div>

      </section>


      {/* =====================================================
          CONTACT DETAILS
      ===================================================== */}

      <section
        id="contact-details"
        className="contact-details-section"
      >

        <div className="contact-container">

          <div className="contact-section-heading">

            <div>

              <div className="contact-section-label">
                <span></span>
                Direct Contact
              </div>

              <h2>
                Reach The
                <br />
                DOFCAN Team
              </h2>

            </div>

            <p>
              Whether you need training support, equipment
              inspection, certification assistance, or general
              project information, use any of the contact channels
              below.
            </p>

          </div>


          <div className="contact-details-grid">

            {/* EMAIL */}

            <article className="contact-detail-card">

              <div className="contact-detail-top">

                <div className="contact-detail-icon">
                  <FaEnvelope />
                </div>

                <span>01</span>

              </div>

              <span className="contact-detail-label">
                EMAIL
              </span>

              <h3>
                Send Us An Email
              </h3>

              <p>
                For enquiries, service requests, verification
                support, and other correspondence.
              </p>

              <a
                href="mailto:dofcanoilserve@yahoo.com"
                className="contact-detail-link"
              >
                dofcanoilserve@yahoo.com
                <FaArrowRight />
              </a>

            </article>


            {/* PHONE */}

            <article className="contact-detail-card">

              <div className="contact-detail-top">

                <div className="contact-detail-icon">
                  <FaPhone />
                </div>

                <span>02</span>

              </div>

              <span className="contact-detail-label">
                TELEPHONE
              </span>

              <h3>
                Speak With Our Team
              </h3>

              <p>
                Contact our team directly for enquiries and
                service-related discussions.
              </p>

              <a
                href="tel:+2348036726334"
                className="contact-detail-link"
              >
                +234 (0) 803 672 6334
                <FaArrowRight />
              </a>

            </article>


            {/* LOCATION */}

            <article className="contact-detail-card">

              <div className="contact-detail-top">

                <div className="contact-detail-icon">
                  <FaMapMarkerAlt />
                </div>

                <span>03</span>

              </div>

              <span className="contact-detail-label">
                LOCATION
              </span>

              <h3>
                Lagos, Nigeria
              </h3>

              <p>
                Our current contact information identifies Lagos,
                Nigeria as our location.
              </p>

              <button
                type="button"
                className="contact-detail-link contact-detail-button"
                onClick={() =>
                  document
                    .getElementById('contact-location')
                    ?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    })
                }
              >
                View Location
                <FaArrowRight />
              </button>

            </article>

          </div>

        </div>

      </section>


      {/* =====================================================
          ENQUIRY SECTION
      ===================================================== */}

      <section
        id="contact-form"
        className="contact-enquiry-section"
      >

        <div className="contact-container">

          <div className="contact-enquiry-wrapper">

            {/* LEFT SIDE */}

            <div className="contact-enquiry-info">

              <div className="contact-request-badge">
                <FaClipboardCheck />
                Enquiry Desk
              </div>

              <h2>
                Tell Us
                <br />
                What You Need.
              </h2>

              <p>
                Share your requirements through the enquiry form.
                Provide enough information for our team to
                understand the nature of your request.
              </p>


              <div className="contact-service-list">

                <div>

                  <span>
                    <FaGraduationCap />
                  </span>

                  <section>
                    <small>TRAINING</small>

                    <strong>
                      Professional Training Programmes
                    </strong>
                  </section>

                </div>


                <div>

                  <span>
                    <FaClipboardCheck />
                  </span>

                  <section>
                    <small>INSPECTION</small>

                    <strong>
                      Equipment Inspection Services
                    </strong>
                  </section>

                </div>


                <div>

                  <span>
                    <FaCertificate />
                  </span>

                  <section>
                    <small>CERTIFICATION</small>

                    <strong>
                      Certification &amp; Verification Support
                    </strong>
                  </section>

                </div>

              </div>


              <div className="contact-response-note">

                <FaShieldAlt />

                <div>
                  <strong>
                    Clear Requirements
                  </strong>

                  <span>
                    Provide your details and enquiry so the team
                    can understand what support you require.
                  </span>
                </div>

              </div>

            </div>


            {/* RIGHT FORM */}

            <div className="contact-form-card">

              <div className="contact-form-header">

                <div>

                  <span>
                    CONTACT REQUEST
                  </span>

                  <h3>
                    Send An Enquiry
                  </h3>

                </div>

                <FaEnvelope />

              </div>


              {/* FORM TABS */}

              <div className="contact-form-tabs">

                <button
                  type="button"
                  className={
                    formMode === 'general'
                      ? 'active'
                      : ''
                  }
                  onClick={() => {
                    setFormMode('general');
                    setSubmitted(false);
                  }}
                >
                  General Enquiry
                </button>

                <button
                  type="button"
                  className={
                    formMode === 'service'
                      ? 'active'
                      : ''
                  }
                  onClick={() => {
                    setFormMode('service');
                    setSubmitted(false);
                  }}
                >
                  Request A Service
                </button>

              </div>


              {!submitted ? (

                <form onSubmit={handleSubmit}>

                  <div className="contact-form-grid">

                    <label className="contact-field">

                      <span>
                        Full Name
                      </span>

                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="Enter your full name"
                      />

                    </label>


                    <label className="contact-field">

                      <span>
                        Email Address
                      </span>

                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="Enter your email address"
                      />

                    </label>


                    <label className="contact-field">

                      <span>
                        Phone Number
                      </span>

                      <input
                        type="tel"
                        name="phone"
                        placeholder="+234..."
                      />

                    </label>


                    <label className="contact-field">

                      <span>
                        Company / Organisation
                      </span>

                      <input
                        type="text"
                        name="company"
                        placeholder="Company or organisation"
                      />

                    </label>


                    {formMode === 'service' && (

                      <label className="contact-field contact-field-full">

                        <span>
                          Service Required
                        </span>

                        <select name="service" defaultValue="">

                          <option
                            value=""
                            disabled
                          >
                            Select a service
                          </option>

                          <option>
                            Mobile Crane Operator Training
                          </option>

                          <option>
                            Crane Inspector Certification
                          </option>

                          <option>
                            Safe Rigging &amp; Lifting
                          </option>

                          <option>
                            Equipment Inspection &amp; Certification
                          </option>

                          <option>
                            Calibration
                          </option>

                          <option>
                            Load Testing
                          </option>

                          <option>
                            Pressure Testing
                          </option>

                        </select>

                      </label>

                    )}


                    <label className="contact-field contact-field-full">

                      <span>
                        Subject
                      </span>

                      <input
                        type="text"
                        name="subject"
                        required
                        placeholder="What is your enquiry about?"
                      />

                    </label>


                    <label className="contact-field contact-field-full">

                      <span>
                        Message
                      </span>

                      <textarea
                        name="message"
                        required
                        rows="6"
                        placeholder="Tell us about your requirements..."
                      />

                    </label>

                  </div>


                  {/* Spam trap: hidden from real visitors */}
                  <input
                    type="text"
                    name="honeypot"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    style={{ display: 'none' }}
                  />


                  {error && (
                    <p className="contact-form-error" role="alert">
                      {error}
                    </p>
                  )}


                  <div className="contact-form-bottom">

                    <span>
                      <FaShieldAlt />
                      Please provide accurate contact information.
                    </span>

                    <button type="submit" disabled={sending}>

                      {sending ? 'Sending...' : 'Submit Enquiry'}
                      <FaArrowRight />

                    </button>

                  </div>

                </form>

              ) : (

                <div className="contact-success">

                  <div className="contact-success-icon">
                    <FaCheckCircle />
                  </div>

                  <span>
                    ENQUIRY STATUS
                  </span>

                  <h3>
                    Enquiry Received
                  </h3>

                  <p>
                    Thank you for contacting Dofcan Oilserve
                    Limited. Your enquiry has been sent to our
                    team and we will respond to you shortly.
                  </p>

                  <button
                    type="button"
                    onClick={resetForm}
                  >
                    Send Another Enquiry
                  </button>

                </div>

              )}

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          CONTACT PROCESS
      ===================================================== */}

      <section className="contact-process-section" data-reveal>

        <div className="contact-container">

          <div className="contact-centred-heading">

            <span>
              WHAT HAPPENS NEXT
            </span>

            <h2>
              A Clear Path From
              <br />
              Enquiry To Support
            </h2>

            <p>
              Once you contact the team, your requirements can be
              reviewed and the appropriate service discussion can
              follow.
            </p>

          </div>


          <div className="contact-process-grid">

            <article className="contact-process-card">

              <span className="contact-process-number">
                01
              </span>

              <div className="contact-process-icon">
                <FaEnvelope />
              </div>

              <h3>
                Send Your Enquiry
              </h3>

              <p>
                Provide your contact details and explain the
                service or information you require.
              </p>

            </article>


            <article className="contact-process-card">

              <span className="contact-process-number">
                02
              </span>

              <div className="contact-process-icon">
                <FaClipboardCheck />
              </div>

              <h3>
                Requirements Review
              </h3>

              <p>
                Your enquiry can be reviewed to understand the
                scope and nature of the request.
              </p>

            </article>


            <article className="contact-process-card">

              <span className="contact-process-number">
                03
              </span>

              <div className="contact-process-icon">
                <FaPhone />
              </div>

              <h3>
                Discuss The Request
              </h3>

              <p>
                Further information can be exchanged through the
                available contact channels.
              </p>

            </article>


            <article className="contact-process-card">

              <span className="contact-process-number">
                04
              </span>

              <div className="contact-process-icon">
                <FaTools />
              </div>

              <h3>
                Determine Next Steps
              </h3>

              <p>
                The appropriate service or support requirements
                can then be discussed with you.
              </p>

            </article>

          </div>

        </div>

      </section>


      {/* =====================================================
          LOCATION
      ===================================================== */}

      <section
        id="contact-location"
        className="contact-location-section"
      >

        <div className="contact-container">

          <div className="contact-section-heading">

            <div>

              <div className="contact-section-label">
                <span></span>
                Our Location
              </div>

              <h2>
                Find Us In
                <br />
                Lagos, Nigeria
              </h2>

            </div>

            <p>
              Use the map below to view the Lagos location area
              associated with our current contact information.
            </p>

          </div>


          <div className="contact-map-wrapper">

            <div className="contact-map">

              <iframe
                title="Dofcan Oilserve Lagos location"
                src={mapSrc}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />

              <div className="contact-map-badge">

                <FaMapMarkerAlt />

                <div>
                  <span>
                    LOCATION
                  </span>

                  <strong>
                    Lagos, Nigeria
                  </strong>
                </div>

              </div>

            </div>


            <div className="contact-location-info">

              <div className="contact-location-icon">
                <FaMapMarkerAlt />
              </div>

              <span>
                DOFCAN OILSERVE LIMITED
              </span>

              <h3>
                Lagos, Nigeria
              </h3>

              <p>
                Our current website contact information identifies
                Lagos, Nigeria as the location.
              </p>


              <div className="contact-location-details">

                <div>

                  <FaEnvelope />

                  <section>
                    <small>Email</small>

                    <a href="mailto:dofcanoilserve@yahoo.com">
                      dofcanoilserve@yahoo.com
                    </a>
                  </section>

                </div>


                <div>

                  <FaPhone />

                  <section>
                    <small>Phone</small>

                    <a href="tel:+2348036726334">
                      +234 (0) 803 672 6334
                    </a>
                  </section>

                </div>


                <div>

                  <FaClock />

                  <section>
                    <small>Contact</small>

                    <strong>
                      Contact us for enquiries
                    </strong>
                  </section>

                </div>

              </div>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          FINAL CTA
      ===================================================== */}

      <section className="contact-final-cta" data-reveal>

        <div className="contact-container">

          <div className="contact-final-grid">

            <div>

              <span>
                DOFCAN OILSERVE LIMITED
              </span>

              <h2>
                Have A Requirement?
                <br />
                Let&apos;s Talk.
              </h2>

              <p>
                Contact our team for training, inspection,
                certification, verification support, and
                project-related enquiries.
              </p>

            </div>


            <div className="contact-final-actions">

              <a href="mailto:dofcanoilserve@yahoo.com">

                Email Our Team
                <FaEnvelope />

              </a>


              <a href="tel:+2348036726334">

                Call +234 (0) 803 672 6334
                <FaPhone />

              </a>

            </div>

          </div>

        </div>

      </section>

    </div>
  );
}